import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useAuth } from '../AuthContext';
import {
  aoChegarNovas,
  caminhoInterno,
  carregarNotificacoes,
  configurarNotificacoes,
  reiniciarNotificacoes,
  verificarNovas,
} from '../utils/notificacoes';
import { sincronizarPush } from '../utils/pushNativo';
import tokens from '../theme/tokens';

const INTERVALO = 60 * 1000; // verificação periódica com o app aberto (o push avisa na hora)

/**
 * Notificações com o app aberto: carrega a lista ao entrar, verifica novidades a cada minuto,
 * quando a tela volta ao primeiro plano e quando chega um push (mensagem do service worker),
 * e mostra um aviso no topo da tela.
 */
const NotificacoesMonitor = () => {
  const { isAuthenticated, userLogado, token } = useAuth();
  const navigate = useNavigate();
  const [aviso, setAviso] = useState(null); // { mensagem, link, quantidade }
  const jogador = isAuthenticated && userLogado?._id && !['Arbitro', 'Goleiro'].includes(userLogado.perfil);

  useEffect(() => {
    if (!jogador) {
      reiniciarNotificacoes();
      return undefined;
    }
    configurarNotificacoes(token);
    carregarNotificacoes();
    sincronizarPush();

    const verificarSeVisivel = () => document.visibilityState === 'visible' && verificarNovas();
    const intervalo = setInterval(verificarSeVisivel, INTERVALO);
    document.addEventListener('visibilitychange', verificarSeVisivel);
    const doServiceWorker = (evento) => evento.data && evento.data.tipo === 'notificacao' && verificarNovas();
    if ('serviceWorker' in navigator) navigator.serviceWorker.addEventListener('message', doServiceWorker);
    const cancelar = aoChegarNovas((novas) => setAviso({ ...novas[0], quantidade: novas.length }));

    return () => {
      clearInterval(intervalo);
      document.removeEventListener('visibilitychange', verificarSeVisivel);
      if ('serviceWorker' in navigator) navigator.serviceWorker.removeEventListener('message', doServiceWorker);
      cancelar();
    };
  }, [jogador, token]);

  const abrir = () => {
    const destino = aviso.quantidade > 1 ? '/notificacoes' : caminhoInterno(aviso.link) || '/notificacoes';
    setAviso(null);
    navigate(destino);
  };

  return (
    <Snackbar
      open={Boolean(aviso)}
      autoHideDuration={8000}
      onClose={(e, motivo) => motivo !== 'clickaway' && setAviso(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ top: { xs: 72, sm: 80 } }}
    >
      {aviso ? (
        <Paper elevation={8} sx={{ width: 'min(92vw, 420px)', p: 1.5, borderLeft: `4px solid ${tokens.color.brand}` }}>
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Box sx={{ color: tokens.color.brand, pt: 0.25 }}><NotificationsActiveRoundedIcon /></Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700}>
                {aviso.quantidade > 1 ? `${aviso.quantidade} novas notificações` : 'Nova notificação'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {aviso.mensagem}
              </Typography>
              <Button size="small" onClick={abrir} sx={{ mt: 0.5, ml: -1 }}>Ver</Button>
            </Box>
            <IconButton size="small" aria-label="Fechar" onClick={() => setAviso(null)}><CloseRoundedIcon fontSize="small" /></IconButton>
          </Stack>
        </Paper>
      ) : (
        <span />
      )}
    </Snackbar>
  );
};

export default NotificacoesMonitor;
